/**
 * Shared PostHog wrapper for the browser surfaces (extension side panel, web
 * page). The SDK is an implementation detail behind this module — call sites
 * use `capture(eventName, props)` with registry names only; every payload goes
 * through `buildEventPayload` (allowlist + forbidden-key strip + event_class
 * stamp) before it can reach the SDK.
 *
 * Privacy contract (docs/analytics/event-spec.md §0):
 * - `enabled: false` (the ANALYTICS_ENABLED build flag) → permanent no-op.
 * - Opt-out is checked BEFORE the SDK is constructed, not after.
 * - Fail open, silently: no analytics error may ever affect app behavior.
 * - `$geoip_disable` on every event; project-level anonymize_ips is also on.
 */
import PostHog from "posthog-js-lite";
import {
  type AnalyticsEventName,
  buildEventPayload,
  POSTHOG_HOST,
  POSTHOG_PROJECT_KEY,
} from "./generated";

export interface AnalyticsInit {
  surface: "extension" | "web";
  appVersion: string;
  /** ANALYTICS_ENABLED build flag. False → this module never touches the SDK. */
  enabled: boolean;
  /** Read the persisted opt-out BEFORE init; opted-out users never construct the SDK. */
  getOptedOut: () => Promise<boolean>;
  /** Anonymous device UUID used as distinct_id (undefined → events stay anonymous). */
  getDistinctId: () => Promise<string | undefined>;
}

export interface ReflectoAnalytics {
  capture(event: AnalyticsEventName, props?: Record<string, unknown>): void;
  /** Merge this device's person with the paired phone's at pairing_completed. */
  aliasToPhone(androidDeviceId: string): void;
  /** Persisting the flag is the caller's job; this halts/resumes capture immediately. */
  setOptedOut(optedOut: boolean): void;
  /** Feature-flag seam for the future Pro rollout. Stub: always the default. */
  isFlagEnabled(key: string, defaultValue: boolean): boolean;
}

export function createAnalytics(init: AnalyticsInit): ReflectoAnalytics {
  let sdk: PostHog | null = null;
  let halted = !init.enabled;

  const ensureSdk = async () => {
    if (sdk) return;
    sdk = new PostHog(POSTHOG_PROJECT_KEY, {
      host: POSTHOG_HOST,
      flushAt: 5,
      flushInterval: 3000,
      persistence: "memory",
    });
    const distinctId = await init.getDistinctId();
    if (distinctId) sdk.identify(distinctId);
  };

  // Single lazy init chain; every operation queues behind it so ordering is
  // preserved and nothing runs before the opt-out check resolves.
  let chain: Promise<void> = init.enabled
    ? (async () => {
        if (await init.getOptedOut()) {
          halted = true;
          return;
        }
        await ensureSdk();
      })()
    : Promise.resolve();
  chain = chain.catch(() => {
    halted = true;
  });

  // Halt gates at ENQUEUE time only — a run-time re-check would drop the final
  // opt-out toggle event accepted just before a same-tick setOptedOut(true).
  const enqueue = (op: () => void, opts: { evenIfHalted?: boolean } = {}) => {
    if (halted && !opts.evenIfHalted) return;
    chain = chain
      .then(() => {
        if (sdk) op();
      })
      .catch(() => {});
  };

  return {
    capture(event, props = {}) {
      try {
        const payload = buildEventPayload(event, props);
        enqueue(() => {
          sdk?.capture(payload.name, {
            ...payload.properties,
            surface: init.surface,
            app_version: init.appVersion,
            $geoip_disable: true,
          });
        });
      } catch {
        // fail open
      }
    },
    aliasToPhone(androidDeviceId) {
      enqueue(() => sdk?.alias(androidDeviceId));
    },
    setOptedOut(optedOut) {
      if (optedOut) {
        // Flush the already-queued opt-out toggle event, then halt.
        enqueue(() => sdk?.flush().catch(() => {}), { evenIfHalted: true });
        halted = true;
      } else if (init.enabled) {
        halted = false;
        // If the user was opted out at startup the SDK was never constructed.
        chain = chain.then(() => ensureSdk()).catch(() => {});
      }
    },
    isFlagEnabled(_key, defaultValue) {
      return defaultValue;
    },
  };
}
